import React, { useEffect, useRef, useState } from 'react';

const GLASS_TOAST_EVENT = 'salew:glass-toast';
const GLASS_TOAST_HIDE_EVENT = 'salew:glass-toast-hide';
const GLASS_TOAST_QUEUE_KEY = '__salewGlassToastQueue';
const GLASS_TOAST_READY_KEY = '__salewGlassToastReady';

const normalizeToast = (message, options = {}) => ({
  id: options.id || `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  message: String(message || '').trim(),
  variant: String(options.variant || 'info').trim() || 'info',
  duration: options.duration === undefined ? 1800 : Number(options.duration),
});

export const showGlassToast = (message, options = {}) => {
  if (typeof window === 'undefined') return;

  const toast = normalizeToast(message, options);
  if (!toast.message) return;

  if (!window[GLASS_TOAST_READY_KEY]) {
    window[GLASS_TOAST_QUEUE_KEY] = window[GLASS_TOAST_QUEUE_KEY] || [];
    window[GLASS_TOAST_QUEUE_KEY].push(toast);
    return;
  }

  window.dispatchEvent(new CustomEvent(GLASS_TOAST_EVENT, { detail: toast }));
};

export const hideGlassToast = (id) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(GLASS_TOAST_HIDE_EVENT, { detail: { id } }));
};

const GlassToastViewport = () => {
  const [toast, setToast] = useState(null);
  const [persistentToast, setPersistentToast] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const showToast = (nextToast) => {
      if (!nextToast?.message) return;
      const isPersistent = Number(nextToast.duration) <= 0;

      if (isPersistent) {
        setPersistentToast(nextToast);
        return;
      }

      const duration = Math.max(900, Number(nextToast.duration || 1800));

      clearTimer();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setToast((prev) => (prev?.id === nextToast.id ? null : prev));
      }, duration);
      setToast({ ...nextToast, duration });
    };

    const handleToast = (event) => {
      showToast(event.detail);
    };

    const handleHideToast = (event) => {
      const targetId = event?.detail?.id;
      setPersistentToast((prev) => (!targetId || prev?.id === targetId ? null : prev));
      setToast((prev) => {
        if (targetId && prev?.id !== targetId) return prev;
        clearTimer();
        return null;
      });
    };

    window[GLASS_TOAST_READY_KEY] = true;
    window.addEventListener(GLASS_TOAST_EVENT, handleToast);
    window.addEventListener(GLASS_TOAST_HIDE_EVENT, handleHideToast);

    const queuedToasts = Array.isArray(window[GLASS_TOAST_QUEUE_KEY])
      ? window[GLASS_TOAST_QUEUE_KEY].splice(0)
      : [];
    queuedToasts.forEach(showToast);

    return () => {
      clearTimer();
      window.removeEventListener(GLASS_TOAST_EVENT, handleToast);
      window.removeEventListener(GLASS_TOAST_HIDE_EVENT, handleHideToast);
      window[GLASS_TOAST_READY_KEY] = false;
    };
  }, []);

  const activeToast = toast || persistentToast;
  if (!activeToast) return null;

  const isPersistent = !toast && Boolean(persistentToast);

  return (
    <div
      className={`glass-toast-message glass-toast-message--${activeToast.variant || 'info'} ${isPersistent ? 'glass-toast-message--persistent' : 'glass-toast-message--transient'}`}
      style={!isPersistent ? { '--glass-toast-duration': `${activeToast.duration || 1800}ms` } : undefined}
      role="status"
      aria-live="polite"
    >
      {activeToast.message}
    </div>
  );
};

export default GlassToastViewport;
